import { RefreshCw } from "lucide-react";

import { MonthPicker } from "@/components/MonthPicker";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/cn";
import { ViewMode } from "@/lib/types";

interface PageHeaderProps {
  title: string;
  lastUpdated?: string;
  monthKey: string;
  onMonthChange: (key: string) => void;
  isSyncing: boolean;
  syncFailed: boolean;
  jobError?: string;
  onSync: () => void;
  hasWeekly?: boolean;
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
}

export function PageHeader({
  title,
  lastUpdated,
  monthKey,
  onMonthChange,
  isSyncing,
  syncFailed,
  jobError,
  onSync,
  hasWeekly,
  viewMode,
  onViewModeChange,
}: PageHeaderProps) {
  const buttonDisabled = isSyncing;
  const tooltip = syncFailed
    ? jobError ?? "Sync failed"
    : `Sync report for ${monthKey}`;
  return (
    <div className="relative z-20 flex flex-wrap items-center justify-between border-b border-border bg-white/80 backdrop-blur px-4 sm:px-8 py-3 gap-2 sm:gap-4 shrink-0">
      <div className="text-sm font-semibold text-ink-900">{title}</div>
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs text-slate-500">
        {lastUpdated && (
          <span className="numeric-value text-[11px] hidden sm:inline-block">Updated {formatDateTime(lastUpdated)}</span>
        )}
        
        {hasWeekly && viewMode && onViewModeChange && (
          <div className="flex items-center rounded-md border border-slate-200 bg-slate-50 p-0.5">
            <button
              type="button"
              onClick={() => onViewModeChange("month")}
              className={cn(
                "px-2 py-1 rounded-sm transition-colors text-xs",
                viewMode === "month" ? "bg-white shadow-sm font-medium text-slate-900" : "text-slate-500 hover:text-slate-700"
              )}
            >
              Month
            </button>
            <button
              type="button"
              onClick={() => onViewModeChange("current_week")}
              className={cn(
                "px-2 py-1 rounded-sm transition-colors text-xs",
                viewMode === "current_week" ? "bg-white shadow-sm font-medium text-slate-900" : "text-slate-500 hover:text-slate-700"
              )}
            >
              Current Week
            </button>
          </div>
        )}

        <MonthPicker selected={monthKey} onChange={onMonthChange} />
        <button
          type="button"
          onClick={onSync}
          disabled={buttonDisabled}
          title={tooltip}
          className={cn(
            "flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors border shrink-0 min-h-[36px]",
            buttonDisabled
              ? "bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed"
              : syncFailed
                ? "bg-red-50 text-red-600 border-red-200 hover:bg-red-100"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:text-slate-900"
          )}
        >
          <RefreshCw className={cn("h-3.5 w-3.5", isSyncing && "animate-spin")} />
          <span>{isSyncing ? "Syncing…" : syncFailed ? "Failed" : "Sync"}</span>
        </button>
      </div>
    </div>
  );
}
